import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';
import { getProvider, listProviders } from '../../services/git/providers';
import { listGitAccounts, removeGitAccount, hostOf } from '../../services/git/gitAccounts';
import { isVaultLockedError } from '../../services/vaultGate';
import Tooltip from '../ui/Tooltip';
import AccountAvatar from './AccountAvatar';
import AddGitAccountModal from './AddGitAccountModal';
import GitHubAccountRow from './GitHubAccountRow';
import GitAddAccountMenu from './GitAddAccountMenu';

/**
 * Connected accounts (git sync cards 6–8) — one chip in the page header since
 * the redesign (05.10.2026), a segment per account: GitHub (GitHubAccountRow —
 * the browser link, or a personal access token since 07.10.2026) and every
 * account added here for a provider that signs in with a token (GitLab,
 * Gitea: a hosted or own server; several per provider). "+ Other" offers
 * exactly the registered providers that can add one; a new provider shows up
 * here without touching this file.
 */
// The token accounts this session last listed: the Git page shows them at once
// on every visit and refreshes behind them (no flash, no reloaded avatars —
// owner, 04.10.2026). Logins, servers and picture URLs only; tokens stay in the
// Vault. Cleared when the list comes back empty.
let lastAccounts = null;
export function __resetGitAccountsCache() {
  lastAccounts = null;
}

const providerOrNull = (id) => {
  try {
    return getProvider(id);
  } catch {
    return null;
  }
};

export default function GitAccounts() {
  const { t } = useTranslation();
  const [added, setAdded] = useState(() => lastAccounts);
  const [adding, setAdding] = useState(null); // provider being added
  const tokenProviders = listProviders().filter((p) => (p.authKinds || []).includes('token') && p.verifyToken);

  const load = useCallback(async () => {
    const list = await listGitAccounts().catch(() => []);
    lastAccounts = list.length ? list : null;
    setAdded(list);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const signOut = async (id) => {
    try {
      await removeGitAccount(id);
    } catch (e) {
      // The Vault gate was closed: nothing changed, nothing to report.
      if (!isVaultLockedError(e)) throw e;
    }
    load();
  };

  // GitHub by token sits in GitHub's own segment; the other accounts follow it.
  const githubToken = (added || []).find((a) => a.provider === 'github') || null;
  const others = (added || []).filter((a) => a.provider !== 'github');

  return (
    <>
      <div className="git-acct" data-testid="git-accounts">
        <GitHubAccountRow
          tokenAccount={githubToken}
          onTokenSignOut={() => githubToken && signOut(githubToken.id)}
          onUseToken={() => setAdding(getProvider('github'))}
          extra={
            <>
              {others.map((a) => {
                const label = providerOrNull(a.provider)?.label || a.provider;
                const host = hostOf(a.server);
                return (
                  <span className="git-acct-seg" key={a.id} data-testid={`git-account-${a.provider}`}>
                    <AccountAvatar account={a} />
                    <Tooltip content={t('gitAccounts.signedInAs', { login: a.login, host })} side="bottom">
                      <span className="git-acct-name">
                        {label} <b>{a.login}</b>
                        <span className="git-sr"> {t('gitAccounts.signedInAs', { login: a.login, host })}</span>
                      </span>
                    </Tooltip>
                    <Tooltip content={t('gitAccounts.signOutOf', { provider: label })} side="bottom">
                      <button
                        type="button"
                        className="git-acct-btn git-acct-btn--icon"
                        onClick={() => signOut(a.id)}
                        aria-label={t('gitAccounts.signOutAria', { provider: label, login: a.login, host })}
                      >
                        <LogOut size={13} />
                      </button>
                    </Tooltip>
                  </span>
                );
              })}
              <GitAddAccountMenu providers={tokenProviders} githubToken={Boolean(githubToken)} onPick={setAdding} />
            </>
          }
        />
      </div>
      <AddGitAccountModal provider={adding} isOpen={Boolean(adding)} onClose={() => setAdding(null)} onAdded={load} />
    </>
  );
}
